import React, { useState, useEffect } from 'react';
import './Courses.css';
import './courses-suggestions.css';
import coursesData from './data/coursesData.json';

function Courses() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Show scroll to top button after scrolling 500px
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowScrollTop(scrollPosition > 500);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    let filtered = coursesData.courses;

    // Filter by search query first (applies to all courses)
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(course =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    // Then filter by category only if no search query
    else if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    setFilteredCourses(filtered);

    // Generate suggestions
    if (searchQuery.trim() !== '' && searchQuery.length > 1) {
      const allSuggestions = coursesData.courses
        .filter(course =>
          course.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 5)
        .map(course => course.name);
      setSuggestions(allSuggestions);
      setShowSuggestions(allSuggestions.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [selectedCategory, searchQuery]);

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedCourse(null);
    document.body.style.overflow = 'auto';
  };

  const getCategoryInfo = (categoryId) => {
    return coursesData.categories.find(cat => cat.id === categoryId);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      setSearchQuery(suggestions[selectedSuggestionIndex]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="courses-page">
      {/* Hero Section */}
      <section className="courses-hero">
        <div className="hero-background">
          <div className="hero-shape shape-1"></div>
          <div className="hero-shape shape-2"></div>
          <div className="hero-shape shape-3"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">دوراتنا التدريبية</h1>
            <p className="hero-subtitle">
              نقدم لكم مجموعة متنوعة من الدورات التدريبية المتخصصة لتطوير مهاراتكم المهنية والشخصية
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="courses-filters">
        <div className="container">
          <div className="search-box">
            <input
              type="text"
              placeholder="ابحث عن دورة..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedSuggestionIndex(-1);
              }}
              onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={handleKeyDown}
              className="search-input"
            />
            <i className="fas fa-search search-icon"></i>
            {searchQuery && (
              <i 
                className="fas fa-times clear-search-icon" 
                onClick={clearSearch}
              ></i>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                    onClick={() => {
                      setSearchQuery(suggestion);
                      setShowSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }}
                  >
                    <i className="fas fa-search"></i>
                    <span>{suggestion}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="category-filters">
            <button
              className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
                setShowSuggestions(false);
              }}
            >
              <span className="category-icon">🎯</span>
              <span>جميع الدورات</span>
            </button>
            {coursesData.categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setSearchQuery('');
                  setShowSuggestions(false);
                }}
              >
                <span className="category-icon">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="courses-grid">
        <div className="container">
          {filteredCourses.length === 0 ? (
            <div className="no-results">
              <i className="fas fa-search"></i>
              <h3>لا توجد نتائج</h3>
              <p>حاول البحث بكلمات أخرى</p>
            </div>
          ) : (
            <div className="courses-list">
              {filteredCourses.map(course => {
                const category = getCategoryInfo(course.category);
                return (
                  <div
                    key={course.id}
                    className="course-card"
                    onClick={() => handleCourseClick(course)}
                  >
                    <div className="course-header">
                      <span className="course-category">
                        <span className="category-icon">{category.icon}</span>
                        {category.name}
                      </span>
                      <span className="course-sessions">
                        {course.sessions} حصة
                      </span>
                    </div>
                    <h3 className="course-title">{course.name}</h3>
                    <p className="course-description">{course.description}</p>
                    <div className="course-skills">
                      {course.skills.slice(0, 3).map((skill, index) => (
                        <span key={index} className="skill-tag">
                          {skill}
                        </span>
                      ))}
                      {course.skills.length > 3 && (
                        <span className="skill-tag more">
                          +{course.skills.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="course-footer">
                      <div className="course-price">
                        <span className="price-amount">{course.price}</span>
                        <span className="price-currency">درهم</span>
                        <span className="price-tax">+ {course.tax} ضريبة</span>
                      </div>
                      <button className="view-details-btn">
                        عرض التفاصيل
                        <i className="fas fa-arrow-left"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Course Details Modal */}
      {selectedCourse && (
        <div className="course-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className="modal-header">
              <div className="modal-category">
                <span className="category-icon">
                  {getCategoryInfo(selectedCourse.category).icon}
                </span>
                <span>{getCategoryInfo(selectedCourse.category).name}</span>
              </div>
              <h2 className="modal-title">{selectedCourse.name}</h2>
              <p className="modal-description">{selectedCourse.description}</p>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3 className="section-title">
                  <i className="fas fa-star"></i>
                  المهارات المكتسبة
                </h3>
                <ul className="skills-list">
                  {selectedCourse.skills.map((skill, index) => (
                    <li key={index}>
                      <i className="fas fa-check-circle"></i>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="modal-section">
                <h3 className="section-title">
                  <i className="fas fa-users"></i>
                  الفئة المستهدفة
                </h3>
                <p className="target-audience">{selectedCourse.target}</p>
              </div>

              <div className="modal-info">
                <div className="info-item">
                  <i className="fas fa-calendar-alt"></i>
                  <div>
                    <span className="info-label">عدد الحصص</span>
                    <span className="info-value">{selectedCourse.sessions} حصة</span>
                  </div>
                </div>
                <div className="info-item">
                  <i className="fas fa-money-bill-wave"></i>
                  <div>
                    <span className="info-label">السعر</span>
                    <span className="info-value">{selectedCourse.price} درهم</span>
                  </div>
                </div>
                <div className="info-item">
                  <i className="fas fa-receipt"></i>
                  <div>
                    <span className="info-label">الضريبة</span>
                    <span className="info-value">{selectedCourse.tax} درهم</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <a 
                href={`https://wa.me/971567953704?text=${encodeURIComponent(`مرحباً، أرغب في التسجيل في ${selectedCourse.name}. أرجو التواصل معي للحصول على المزيد من التفاصيل.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="register-btn"
              >
                <i className="fab fa-whatsapp"></i>
                سجل الآن
              </a>
              <button className="contact-btn" onClick={closeModal}>
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop}></button>
      )}
    </div>
  );
}

export default Courses;
